import JSZip from 'jszip'
import { AppStore } from '@/store'
import { saveAs } from 'file-saver'
import { ChartInfo } from '@/store/modules/livechart-reference-store-module'
import { Options } from 'highcharts'
import { detectIE } from './browser'

export const EXPORTING_OPTIONS = {
  enabled: false,
  sourceHeight: 800,
  sourceWidth: 1000
}

export interface ImageObj {
  data: string
  questionId: string
}

export const generateChartFileName = (workspaceName?: string, testId?: string | number, order?: string | number) => {
  const workspaceNameSanitized = workspaceName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || ''
  return `${workspaceNameSanitized}_${testId || ''}_question${order || ''}`
}

export const exportChartToBlob = (store: AppStore): Promise<ImageObj[]> => {
  const promises: Promise<ImageObj>[] = []

  Object.keys(store.getters.livechartRefs.chartRefMap).forEach((qId) => {
    const chartInfo = store.getters.livechartRefs.chartRefMap[qId]
    let exportSettings: Options = {
      title: {
        ...chartInfo.exporting.chartOptions!.title
      },
      subtitle: {
        text: chartInfo.exporting.chartOptions!.subtitle!.text
      }
    }

    if (chartInfo.chartRef.chart.series[0].type !== 'wordcloud') { // wordclouds don't have x axis labels
      exportSettings = {
        ...exportSettings,
        ...chartInfo.exporting.chartOptions
      }
    }
    const png = svgToPng(chartInfo.chartRef.chart.getSVG(exportSettings), qId)
    promises.push(png)
  })

  return Promise.all(promises)
}

export const exportChartsToZip = (store: AppStore) => {
  const zip = new JSZip()

  const blobPromises = exportChartToBlob(store)
  blobPromises.then((pngs) => {
    const { testId } = store.state.surveyConfiguration
    const { workspace } = store.state.workspace
    const workspaceNameSanitized = workspace?.name.replace(/[^a-z0-9]/gi, '_').toLowerCase() || store.state.surveyConfiguration.workspaceId
    const folderTitle = `${workspaceNameSanitized}_${testId}.zip`

    pngs.forEach((png, i) => {
      const fileTitle = generateChartFileName(workspaceNameSanitized, testId!, i + 1) + '.png'
      const baseString = getBase64String(png.data)
      zip.file(fileTitle, baseString, {
        base64: true
      })
    })

    zip.generateAsync({ type: 'blob' })
      .then(function (content: any) {
        saveAs(content, folderTitle)
      })
  })
}

export const svgToPng = (svgText: string, questionId: string): Promise<ImageObj> => {
  return new Promise(resolve => {
    const blob = new Blob([svgText], {
      type: 'image/svg+xml;charset=utf-8'
    })

    const URL = window.URL || window.webkitURL || window
    const blobURL = URL.createObjectURL(blob)

    let match = svgText.match(/height=\"(\d+)/m)
    const height = match && match[1] ? parseInt(match[1], 10) : 200
    match = svgText.match(/width=\"(\d+)/m)
    const width = match && match[1] ? parseInt(match[1], 10) : 200

    const image = new Image()
    image.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.height = height
      canvas.width = width
      const context = canvas.getContext('2d')!
      detectIE() ? await drawIE(context, svgText) : draw(context, width, height, image)
      resolve({ data: canvas.toDataURL(), questionId })
    }
    image.src = blobURL
  })
}

const draw = async (context: CanvasRenderingContext2D, width: number, height: number, image: HTMLImageElement) => {
  context!.drawImage(image, 0, 0, width, height)
}

const drawIE = async (context: CanvasRenderingContext2D, svgText: string) => {
  const { default: Canvg } = await import('canvg')
  const v = await Canvg.from(context!, svgText)
  await v.render()
}

export const exportIndividualChart = (chartInstance: ChartInfo, type: 'image/png' | 'image/svg+xml'): void => {
  chartInstance.chartRef.chart.exportChart({ type, filename: chartInstance.fileName }, {})
}

export const getBase64String = (dataURL: string) => {
  const idx = dataURL.indexOf('base64,') + 'base64,'.length
  return dataURL.substring(idx)
}
